import React, { Fragment, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import FancyDropdown, {
  FancyDropdownMenu,
  FancyDropdownMenuItem
} from 'dpl/components/FancyDropdown';
import { isEmpty, get } from 'dpl/shared/utils/object';
import { groupBy } from 'dpl/shared/utils';
import { getOrderedObjectEntries } from 'dpl/shared/utils/breeder_app';
import ErrorWrapper from 'dpl/form/components/ErrorWrapper';
import Icon from 'dpl/common/components/Icon';

const ORDERED_BREED_TITLES = ['Pure Breeds', 'Mixed Breeds'];

export function getGroupedBreeds(matchText, availableBreeds) {
  if (matchText) {
    matchText = matchText.toLowerCase();
    availableBreeds = availableBreeds.filter(b =>
      b.name.toLowerCase().includes(matchText)
    );
  }

  const breedsByTitle = groupBy(availableBreeds, ({ hybrid }) => {
    if (hybrid) {
      return 'Mixed Breeds';
    }
    if (hybrid === false) {
      return 'Pure Breeds';
    }
    return '';
  });

  return getOrderedObjectEntries(breedsByTitle, ORDERED_BREED_TITLES);
}
export default function BreedSelect({
  availableBreeds,
  className,
  disabled,
  displayErrors,
  errors,
  name,
  id,
  omitIcon,
  disablePlaceholder,
  onSelect,
  selectedId,
  newDesignSystemStyles,
  defaultIsOpen,
  placeholder
}) {
  const [textInput, setTextInput] = useState(null);
  const [isOpen, setIsOpen] = useState(defaultIsOpen);
  const [breedsById, setBreedsById] = useState({});
  const [breedsByNames, setBreedsByNames] = useState({});
  const [breedsByTitle, setBreedsByTitle] = useState([]);
  const [selectedBreedId, setSelectedBreedId] = useState(selectedId);

  const inputRef = useRef();

  const selectedBreedName = get(breedsById[selectedBreedId], '0.name', '');

  if (selectedId !== selectedBreedId) {
    setSelectedBreedId(selectedId);
  }

  useEffect(() => {
    if (isEmpty(breedsById)) {
      setBreedsById(groupBy(availableBreeds, 'id'));
      setBreedsByNames(groupBy(availableBreeds, 'name'));
    }
  }, [availableBreeds]);

  function breedById(breedId) {
    return get(breedsById, `${breedId}`, [])[0];
  }
  function breedByName(breedName) {
    return get(breedsByNames, `${breedName}`, [])[0];
  }

  function handleInputBlur() {
    const textInputBreed = breedByName(selectedBreedName);
    const selectedBreed = breedById(selectedBreedId);
    setTextInput(null);

    if (textInputBreed && selectedBreed) {
      if (textInputBreed.id !== selectedBreed.id) {
        setSelectedBreedId(textInputBreed.id);
        onSelect(textInputBreed.id);
      }
      return;
    }

    if (textInputBreed && !selectedBreed) {
      setSelectedBreedId(textInputBreed.id);
      onSelect(textInputBreed.id);
      return;
    }

    if (!textInputBreed && selectedBreed) {
      if (disablePlaceholder) {
        setSelectedBreedId(selectedBreed.name);
      } else {
        setSelectedBreedId(null);
        onSelect(null);
      }
    }
  }

  function handleClickOption(breedId) {
    return event => {
      event.preventDefault();
      inputRef.current.blur();
      onSelect(breedId);
    };
  }

  function handleInputChange(event) {
    const breedText = event.target.value;
    setTextInput(breedText);
    setBreedsByTitle(getGroupedBreeds(breedText, availableBreeds));
    setIsOpen(true);
  }

  function handleDropdownChange(isDropdownOpen) {
    setIsOpen(isDropdownOpen);
  }

  function handleInputFocus(event) {
    window.setTimeout(() => event.target.select(), 1);

    setBreedsByTitle(getGroupedBreeds(selectedBreedName, availableBreeds));
    setIsOpen(true);
  }

  return (
    <div className="BreedSelect">
      <FancyDropdown onChange={handleDropdownChange} isOpen={isOpen}>
        <ErrorWrapper errors={errors} isShown={displayErrors}>
          <div className="w-100">
            <input
              ref={inputRef}
              className={classnames(
                'ba br1 b--light-gray pv2 ph2 w-100',
                className,
                {
                  'br--top': isOpen,
                  'br3 pv3 ph4 font-16': newDesignSystemStyles
                }
              )}
              name={name}
              autoComplete="off"
              type="text"
              value={
                typeof textInput === 'string' ? textInput : selectedBreedName
              }
              placeholder={placeholder}
              onBlur={handleInputBlur}
              onFocus={handleInputFocus}
              onChange={handleInputChange}
              autoFocus={defaultIsOpen}
              disabled={disabled || availableBreeds.length === 0}
              id={id}
              data-test-id="breed_select_input"
            />
            {!omitIcon && (
              <Icon
                className="mr2 pointer-events-none top-0 right-0 transform-center"
                name="fat-arrow-down"
              />
            )}
          </div>
        </ErrorWrapper>
        <div role="presentation" onMouseDown={e => e.preventDefault()}>
          <FancyDropdownMenu className="BreedSelect__dropdownMenu ba b--light-gray br2 br--bottom">
            {breedsByTitle.map(([title, breeds = []]) => (
              <Fragment key={title}>
                {breeds.length > 0 && title && (
                  <p className="mv3 f2 black-50">{title}</p>
                )}
                {breeds.map(breed => (
                  <FancyDropdownMenuItem key={breed.id} itemKey={breed.id}>
                    <button
                      data-test-id={`breed_select_${breed.id}`}
                      type="button"
                      onClick={handleClickOption(breed.id)}
                      className="pv2 ph2 w-100"
                    >
                      {breed.name}
                    </button>
                  </FancyDropdownMenuItem>
                ))}
              </Fragment>
            ))}
          </FancyDropdownMenu>
        </div>
      </FancyDropdown>
    </div>
  );
}

BreedSelect.propTypes = {
  disablePlaceholder: PropTypes.bool,
  availableBreeds: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      hybrid: PropTypes.bool
    })
  ).isRequired,
  disabled: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  selectedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // eslint-disable-next-line react/forbid-prop-types
  errors: PropTypes.array,
  displayErrors: PropTypes.bool,
  name: PropTypes.string,
  className: PropTypes.string,
  id: PropTypes.string,
  omitIcon: PropTypes.bool,
  newDesignSystemStyles: PropTypes.bool,
  defaultIsOpen: PropTypes.bool,
  placeholder: PropTypes.string
};

BreedSelect.defaultProps = {
  disablePlaceholder: false,
  disabled: false,
  selectedId: null,
  errors: [],
  displayErrors: false,
  name: 'breed_name',
  className: null,
  id: null,
  omitIcon: false,
  newDesignSystemStyles: false,
  defaultIsOpen: false,
  placeholder: 'Type your breed name'
};
